import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import type { FormDataConvertible } from "@inertiajs/core";
import { LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FdeEditorDialog } from "@/frontend/features/Fde/FdeEditorSurface";
import FdeSelect from "@/frontend/features/Fde/FdeSelect";
import ConnectorIcon from "@/frontend/features/Fde/data/ConnectorIcon";
import { fdeProjectPath, fdeRoutes } from "@/frontend/features/Fde/routes";
import { SourceSchemaFields } from "@/frontend/features/Fde/data/SourceSchemaFields";
import type {
  ForgeConnector,
  ForgeDestination,
  SourceSchema,
} from "@/frontend/features/Fde/data/types";

interface Props {
  connector: ForgeConnector | null;
  destinations: ForgeDestination[];
  effectiveDestinationId: string | null;
  projectId?: string | null;
  copy: Record<string, string>;
  onClose: () => void;
}

function setNested(
  current: Record<string, unknown>,
  path: string[],
  value: unknown,
) {
  const next = structuredClone(current);
  let cursor = next;
  path.slice(0, -1).forEach((part) => {
    cursor[part] =
      typeof cursor[part] === "object" && cursor[part] ? cursor[part] : {};
    cursor = cursor[part] as Record<string, unknown>;
  });
  cursor[path.at(-1)!] = value;
  return next;
}

export default function SourceSetupDialog({
  connector,
  destinations,
  effectiveDestinationId,
  projectId,
  copy,
  onClose,
}: Props) {
  const [schema, setSchema] = useState<SourceSchema | null>(null);
  const [configuration, setConfiguration] = useState<Record<string, unknown>>(
    {},
  );
  const [name, setName] = useState("");
  const [destinationId, setDestinationId] = useState(
    effectiveDestinationId || "",
  );
  const [intervalSeconds, setIntervalSeconds] = useState("86400");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [streams, setStreams] = useState<string[]>([]);
  const requestId = useRef(crypto.randomUUID());

  useEffect(() => {
    if (!connector) return;
    const controller = new AbortController();
    setName(connector.name);
    requestId.current = crypto.randomUUID();
    setStreams([]);
    setIntervalSeconds(connector.id.startsWith("native:") ? "0" : "86400");
    setDestinationId(effectiveDestinationId || "");
    setConfiguration({});
    setSchema(null);
    setError(null);
    setLoading(true);
    fetch(
      fdeProjectPath(`${fdeRoutes.data}/sources/${encodeURIComponent(connector.id)}/specification`, projectId),
      { signal: controller.signal, headers: { Accept: "application/json" } },
    )
      .then(async (response) => {
        const body = await response.json();
        if (controller.signal.aborted) return;
        if (!response.ok) throw new Error(body.error || copy.spec_error);
        setSchema(body);
        setStreams(body.default_streams || []);
      })
      .catch((reason) => {
        if (reason.name !== "AbortError")
          setError(reason.message || copy.spec_error);
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [connector, copy.spec_error, projectId, effectiveDestinationId]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!connector) return;
    setError(null);
    setSubmitting(true);
    router.post(
      fdeProjectPath(`${fdeRoutes.data}/sources`, projectId),
      {
        source: {
          name,
          source_definition_id: connector.id,
          destination_id: destinationId,
          interval_seconds: intervalSeconds,
          configuration: configuration as FormDataConvertible,
          ...(schema?.available_streams ? { streams, request_id: requestId.current } : {}),
        },
      },
      { onSuccess: onClose, onError: (errors) => setError(String(errors.source || copy.spec_error)), onFinish: () => setSubmitting(false) },
    );
  };

  return (
    <FdeEditorDialog
      open={Boolean(connector)}
      onOpenChange={(open) => {
        if (!open && !submitting) onClose();
      }}
      title={`${copy.connect_title} ${connector?.name || ''}`}
      description={copy.connect_description}
      icon={connector ? <ConnectorIcon iconUrl={connector.icon_url} className="size-8 rounded-lg" imageClassName="size-6" fallbackClassName="size-4" /> : undefined}
      closeLabel={copy.cancel}
      footer={<><Button type="button" variant="outline" disabled={submitting} onClick={onClose}>{copy.cancel}</Button><Button type="submit" form="fde-source-setup-form" disabled={submitting || loading || !schema || !destinationId || (Boolean(schema.available_streams) && streams.length === 0)}>{submitting ? copy.connecting : copy.connect}</Button></>}
    >
        <form id="fde-source-setup-form" onSubmit={submit} className="space-y-6">
          <label className="block space-y-1.5">
            <span className="text-sm font-medium">{copy.connection_name}</span>
            <Input
              value={name}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">{copy.frequency}</span>
              <FdeSelect ariaLabel={copy.frequency} value={intervalSeconds} onValueChange={setIntervalSeconds} options={[{ value: "0", label: copy.frequency_manual }, ...(schema?.recurring_sync_supported === false ? [] : [{ value: "3600", label: copy.frequency_hourly }, { value: "21600", label: copy.frequency_six_hours }, { value: "43200", label: copy.frequency_twelve_hours }, { value: "86400", label: copy.frequency_daily }, { value: "604800", label: copy.frequency_weekly }])]} />
            </div>
            <div className="block space-y-1.5">
              <span className="text-sm font-medium">{copy.destination}</span>
              <FdeSelect ariaLabel={copy.destination} value={destinationId} onValueChange={setDestinationId} placeholder={copy.destination_required} required options={destinations.map((destination) => ({ value: destination.id, label: destination.name }))} />
            </div>
          </div>
          {schema?.available_streams && <fieldset className="space-y-3 border-t border-border pt-5">
            <legend className="text-sm font-semibold">{copy.connection_streams}</legend>
            <p className="text-xs text-muted-foreground">{copy.connection_streams_description}</p>
            <div className="grid gap-2 sm:grid-cols-2">{schema.available_streams.map((stream) => <label key={stream} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={streams.includes(stream)} onChange={(event) => setStreams((current) => event.target.checked ? [...current, stream] : current.filter((name) => name !== stream))} />
              {schema.stream_labels?.[stream] || stream.replace(/_/g, " ")}
            </label>)}</div>
          </fieldset>}
          <div className="border-t border-border pt-5">
            <h3 className="mb-4 text-sm font-semibold">{copy.credentials}</h3>
            {loading && (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <LoaderCircle className="size-4 animate-spin" />
                {copy.loading_spec}
              </div>
            )}
            {error && (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {error}
              </p>
            )}
            {schema && (
              <SourceSchemaFields
                schema={schema}
                value={configuration}
                required={schema.required}
                onChange={(path, value) =>
                  setConfiguration((current) => setNested(current, path, value))
                }
              />
            )}
          </div>
        </form>
    </FdeEditorDialog>
  );
}
